/**
 * What the session page says is happening to an upload, step by step: one pure function over what
 * the page already polls (the frame list, mediaStatus), so every screen that shows progress — the
 * processing card before any result exists, and the Premium tab while premium is on its way —
 * tells the same story with the same words.
 *
 * On-device first, premium on top. Every upload that arrived with its on-device pose gets the
 * on-device result first (key frames, cues, score). A premium upload is then upgraded by the server:
 * the cloud GPU labels every frame again, and the narrated video is rendered (script, voiceover,
 * encode). A premium upload with NO on-device pose (a native-rate technique) goes straight to the
 * cloud, and no on-device step is claimed for it.
 */
import { AnalysisEngine } from "./analysisEngine";

export type JourneyStepKey = "uploaded" | "split" | "analyse" | "result" | "cloud" | "script" | "voiceover" | "encode";
export type JourneyStep = {
  key: JourneyStepKey;
  label: string;
  state: "done" | "active" | "upcoming";
  /** A line under the active step: the frame count, or what it is waiting on. */
  detail?: string;
};

export type JourneyInput = {
  /** The split frames, each with whether its labels are in (uploadFrames). Empty until the split lands. */
  frames: ReadonlyArray<{ labelled: boolean }>;
  /** The on-device result (or any analysis) is out: mediaStatus.analysis. */
  hasAnalysis: boolean;
  /** The narrated video exists: mediaStatus.video. */
  hasVideo: boolean;
  /** The uploader asked for premium: mediaStatus.premiumRequested, or an upgrade just queued. */
  premiumRequested: boolean;
  /** Which engine owns the labels right now: mediaStatus.analysisEngine. */
  analysisEngine: string | undefined;
  /** The stage the server's render is in: mediaStatus.renderStage. */
  renderStage: string | null | undefined;
  /** This page is itself labelling the split frames (ClientLabellingStep is running). */
  labellingOnThisDevice: boolean;
};

export type Journey = {
  steps: JourneyStep[];
  premium: boolean;
  /** Nothing more is coming: a free upload's result is out, or a premium upload's video exists. */
  finished: boolean;
  framesDone: number;
  framesTotal: number;
};

const RENDER_STEPS: JourneyStepKey[] = ["script", "voiceover", "encode"];

/** The render stage names the server writes (RenderStageLog), mapped onto the steps shown. Drawing
 * the overlays (compose) leads straight into the script, so it reads as the script step. */
function renderStep(stage: string | null | undefined): { key: JourneyStepKey; detail?: string } | null {
  switch (stage) {
    case "compose": return { key: "script", detail: "Drawing the overlays" };
    case "narration-tts":
    case "narration-script": return { key: "script" };
    case "narration-voice": return { key: "voiceover" };
    case "ffmpeg-video": return { key: "encode" };
    default: return null;
  }
}

export function sessionJourney(input: JourneyInput): Journey {
  const framesTotal = input.frames.length;
  const framesDone = input.frames.filter((f) => f.labelled).length;
  const percent = framesTotal > 0 ? Math.round((framesDone * 100) / framesTotal) : 0;
  const frameCount = `${framesDone} of ${framesTotal} frames · ${percent}%`;
  const premium = input.premiumRequested;
  const gpuOwnsLabels = input.analysisEngine === AnalysisEngine.Gpu;
  const anyResult = input.hasAnalysis || input.hasVideo;
  // Premium with the GPU already in charge and no on-device result: it never had an on-device pose.
  const straightToCloud = premium && gpuOwnsLabels && !anyResult;

  const steps: JourneyStep[] = [{ key: "uploaded", label: "Uploaded", state: "done" }];
  const splitDone = framesTotal > 0 || anyResult;
  steps.push({ key: "split", label: "Splitting your clip into frames", state: splitDone ? "done" : "active" });

  const allFramesIn = framesTotal > 0 && framesDone >= framesTotal;

  if (!straightToCloud) {
    const analysing = splitDone && !anyResult && !allFramesIn;
    steps.push({
      key: "analyse",
      label: input.labellingOnThisDevice ? "Analysing frames on this device" : "Applying the on-device analysis to each frame",
      state: anyResult || allFramesIn ? "done" : analysing ? "active" : "upcoming",
      detail: analysing && framesTotal > 0 ? frameCount : undefined,
    });
    steps.push({
      key: "result",
      label: "Building your key frames, cues and score",
      state: anyResult ? "done" : allFramesIn ? "active" : "upcoming",
    });
  }

  if (premium) {
    // The cloud pass has started once the GPU owns the labels (the upgrade re-stamps the pose file).
    const cloudWaitsForResult = !straightToCloud && !anyResult;
    const cloudStarting = !straightToCloud && anyResult && !input.hasVideo && !gpuOwnsLabels;
    const cloudRunning = !input.hasVideo && !cloudWaitsForResult && !cloudStarting && splitDone && !allFramesIn;
    const cloudDone = input.hasVideo || (!cloudWaitsForResult && !cloudStarting && allFramesIn);
    steps.push({
      key: "cloud",
      label: "Cloud GPU analysing every frame",
      state: cloudDone ? "done" : cloudStarting || cloudRunning ? "active" : "upcoming",
      detail: cloudStarting ? "Starting…" : cloudRunning && framesTotal > 0 ? frameCount : undefined,
    });

    const rendering = cloudDone && !input.hasVideo;
    const at = rendering ? renderStep(input.renderStage) ?? { key: "script" as JourneyStepKey, detail: "Queued for rendering" } : null;
    const labels: Record<string, string> = {
      script: "Writing the coaching script",
      voiceover: "Recording the voiceover",
      encode: "Encoding your video",
    };
    for (const key of RENDER_STEPS) {
      const state: JourneyStep["state"] = input.hasVideo
        ? "done"
        : at == null
          ? "upcoming"
          : RENDER_STEPS.indexOf(key) < RENDER_STEPS.indexOf(at.key)
            ? "done"
            : key === at.key
              ? "active"
              : "upcoming";
      steps.push({ key, label: labels[key], state, detail: state === "active" ? at?.detail : undefined });
    }
  }

  return {
    steps,
    premium,
    finished: premium ? input.hasVideo : anyResult,
    framesDone,
    framesTotal,
  };
}

/** How often the page should ask the server while this journey is in hand: every 4 s while frames
 * are moving, every 10 s during the render (its stages last minutes), and not at all once finished. */
export function pollEveryMs(journey: Journey): number | null {
  if (journey.finished) return null;
  const active = journey.steps.find((s) => s.state === "active");
  return active && RENDER_STEPS.includes(active.key) ? 10000 : 4000;
}
